class GiftCartSync {
  static #busy = false;
  static #debounceTimer = null;
  static #freeManager = null;
  static #promoManager = null;

  static get busy() {
    return this.#busy;
  }

  static init(freeManager, promoManager) {
    this.#freeManager = freeManager;
    this.#promoManager = promoManager;

    if (typeof subscribe === 'undefined' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (GiftCart.isOwnSource(event.source)) return;
      if (event.cartData) {
        if (this.#freeManager) this.#freeManager.updateCachedCart(event.cartData);
        if (this.#promoManager) this.#promoManager.updateCachedCart(event.cartData);
        this.scheduleSync(event.cartData);
      }
    });
  }

  static scheduleSync(cartData) {
    clearTimeout(this.#debounceTimer);
    this.#debounceTimer = setTimeout(() => {
      this.runSync(cartData);
    }, 150);
  }

  static waitForIdle(timeoutMs) {
    const deadline = Date.now() + (timeoutMs || 2500);
    return new Promise((resolve) => {
      const tick = () => {
        if (!this.#busy || Date.now() >= deadline) resolve();
        else setTimeout(tick, 50);
      };
      tick();
    });
  }

  static async runSync(initialCart) {
    if (this.#busy) return initialCart;
    this.#busy = true;

    if (this.#freeManager) this.#freeManager.isValidatingCart = true;
    if (this.#promoManager) this.#promoManager.isValidatingCart = true;

    try {
      let cart = initialCart || (await GiftCart.fetchCart());
      if (!cart) return null;

      let changed = false;

      if (this.#freeManager?.enabled) {
        this.#freeManager.updateCachedCart(cart);
        const result = await this.#freeManager.enforceCart(cart);
        cart = result.cart;
        changed = changed || result.changed;
      }

      if (this.#promoManager?.enabled) {
        this.#promoManager.updateCachedCart(cart);
        const result = await this.#promoManager.enforceCart(cart);
        cart = result.cart;
        changed = changed || result.changed;
      }

      if (changed) {
        GiftCart.publishCartUpdate('gift-cart-sync', cart);
      }

      return cart;
    } finally {
      this.#busy = false;
      if (this.#freeManager) this.#freeManager.isValidatingCart = false;
      if (this.#promoManager) this.#promoManager.isValidatingCart = false;
    }
  }

  static async enforceOnPageLoad() {
    if (!GiftCart.isCartPage()) return;

    await new Promise((resolve) => setTimeout(resolve, 500));

    let cart = await GiftCart.fetchCart();
    if (!cart) return;

    let changed = false;

    if (this.#freeManager?.enabled) {
      this.#freeManager.updateCachedCart(cart);
      this.#freeManager.previousCartItemCount = cart.items.length;
      const result = await this.#freeManager.enforceCart(cart, { pageLoad: true });
      cart = result.cart;
      changed = changed || result.changed;
    }

    if (this.#promoManager?.enabled) {
      this.#promoManager.updateCachedCart(cart);
      const result = await this.#promoManager.enforceCart(cart);
      cart = result.cart;
      changed = changed || result.changed;
    }

    if (changed) {
      GiftCart.publishCartUpdate('gift-cart-sync', cart);
      await GiftCart.refreshCartSections();
    }
  }

  static async syncBeforeDrawerBuild() {
    const managers = [this.#freeManager, this.#promoManager].filter(Boolean);
    if (!managers.length) return;

    await this.runSync(await GiftCart.fetchCart());
    await this.waitForIdle(2500);
  }
}

window.GiftCartSync = GiftCartSync;

async function initGiftCart() {
  const config = GiftCart.parseConfig();
  const hasFirstOrder = !!config.firstOrderGift?.variantId;
  const hasPromo = !!(config.promoCampaign?.gifts?.length);
  if (!hasFirstOrder && !hasPromo) return;

  const collectionCache = new GiftCollectionCache();
  let freeGiftManager = null;
  if (hasFirstOrder) {
    freeGiftManager = new FreeGiftManager(config, collectionCache);
    await freeGiftManager.resolveEligibility();
    window.freeGiftManager = freeGiftManager;
  }

  let promoGiftManager = null;
  if (hasPromo) {
    promoGiftManager = new PromoGiftManager(config, collectionCache);
    await promoGiftManager.resolveEligibility();
    window.promoGiftManager = promoGiftManager;
  }

  if (!freeGiftManager && !promoGiftManager) return;

  GiftCartSync.init(freeGiftManager, promoGiftManager);
  initGiftCheckoutListeners();
  await GiftCartSync.enforceOnPageLoad();
}

window.syncGiftCartBeforeDrawerBuild = () => GiftCartSync.syncBeforeDrawerBuild();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initGiftCart);
} else {
  initGiftCart();
}
